import OnnxWorker from './onnxWorker.ts?worker';

let bridge: OnnxBridge | null = null;

export function getOnnxBridge(): OnnxBridge {
  if (!bridge) bridge = new OnnxBridge();
  return bridge;
}

export function destroyOnnxBridge(): void {
  bridge?.destroy();
  bridge = null;
}

export class OnnxBridge {
  private worker: Worker;
  private ready = false;
  private requestId = 0;
  private pending = new Map<
    number,
    {
      resolve: (v: { data: Float32Array | Int32Array; dims: readonly number[] }) => void;
      reject: (e: Error) => void;
      timeout: number;
    }
  >();

  constructor() {
    this.worker = new OnnxWorker();
    this.worker.onmessage = (ev: MessageEvent<any>) => {
      const msg = ev.data;
      if (msg.type === 'ready') {
        this.ready = msg.ready;
        return;
      }
      const req = this.pending.get(msg.requestId);
      if (!req) return;
      clearTimeout(req.timeout);
      this.pending.delete(msg.requestId);
      if (msg.type === 'result') {
        req.resolve({ data: msg.data, dims: msg.dims });
      } else {
        req.reject(new Error(msg.reason || 'onnx_error'));
      }
    };
  }

  isReady(): boolean {
    return this.ready;
  }

  warm(): void {
    this.worker.postMessage({ type: 'warm' });
  }

  infer(source: ImageBitmap): Promise<{ data: Float32Array | Int32Array; dims: readonly number[] }> {
    return new Promise((resolve, reject) => {
      const id = ++this.requestId;
      const timeout = window.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error('onnx_timeout'));
      }, 30000);
      this.pending.set(id, { resolve, reject, timeout });
      this.worker.postMessage({ type: 'infer', requestId: id, source }, [source]);
    });
  }

  destroy(): void {
    for (const [, req] of this.pending) {
      clearTimeout(req.timeout);
      req.reject(new Error('destroyed'));
    }
    this.pending.clear();
    this.worker.terminate();
  }
}
